import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  listMembers,
  updateMemberRole,
  deleteMember,
  LEVEL_FOR_ROLE,
  LEVEL_LABEL,
  type Member,
  type StytchRoleId,
} from "../../services/adminService";
import MemberTable from "./MemberTable";
import InviteModal from "./InviteModal";
import ConfirmModal from "./ConfirmModal";
import Toast, { type ToastItem } from "./Toast";

export interface MembersTabProps {
  getToken: () => string | undefined;
}

type ConfirmState =
  | {
      kind: "role-change";
      member: Member;
      nextRole: StytchRoleId;
    }
  | { kind: "deactivate"; member: Member }
  | { kind: "cancel-invite"; member: Member };

const MembersTab: React.FC<MembersTabProps> = ({ getToken }) => {
  const [members, setMembers] = useState<Member[]>([]);
  const [organizations, setOrganizations] = useState<Record<string, string>>({});
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState<ConfirmState | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  // Monotonic request id used to discard stale listMembers responses. When
  // two fetches are in flight (mount + debounced search, or rapid search
  // input), only the most recently-issued one is allowed to write state — so
  // out-of-order resolution can no longer clobber the UI with stale data.
  const requestIdRef = useRef(0);
  // Skip the debounce effect on first commit so it does not double-fetch with
  // the mount effect. Initial load belongs to the mount effect only.
  const isFirstMountRef = useRef(true);

  const pushToast = useCallback((kind: "success" | "error", message: string) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    // Cap the visible queue at 3 so retry storms and rapid role toggles cannot
    // grow the stack unboundedly and overlap visually. slice(-2) keeps the two
    // most recent and appends the new one.
    setToasts((prev) => [...prev.slice(-2), { id, kind, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  }, []);

  const fetchMembers = useCallback(
    async (
      cursor: string | undefined,
      query: string | undefined,
      append: boolean,
      signal?: AbortSignal,
    ) => {
      const myRequestId = ++requestIdRef.current;
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
          setError(null);
        }
        const response = await listMembers(cursor, query, getToken(), signal);
        if (requestIdRef.current !== myRequestId) return; // stale, ignore
        setMembers((prev) =>
          append ? [...prev, ...response.members] : response.members,
        );
        setNextCursor(response.results_metadata?.next_cursor);
        // Org names accumulate rather than replace on "load more" — a later
        // page's map is over the same union of orgs, so merging keeps the
        // table's Organization column filled in even for org names that
        // only arrived on an earlier page.
        setOrganizations((prev) =>
          append ? { ...prev, ...response.organizations } : response.organizations,
        );
      } catch (e: any) {
        // Swallow aborts silently — these are user-initiated (unmount, etc.)
        if (e?.name === "AbortError") return;
        if (requestIdRef.current !== myRequestId) return; // stale, ignore
        if (!append) {
          setError(e?.message ?? "Failed to load members");
        }
      } finally {
        if (requestIdRef.current === myRequestId) {
          if (append) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    [getToken],
  );

  useEffect(() => {
    // Cancel the in-flight request on unmount so we do not write state to an
    // unmounted component (and to drop the wasted RTT entirely).
    const controller = new AbortController();
    fetchMembers(undefined, undefined, false, controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Skip the first commit so we don't race the mount effect with a
    // redundant debounced "" query.
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      return;
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setActiveQuery(searchInput);
      fetchMembers(undefined, searchInput || undefined, false);
    }, 400);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const reloadList = () => fetchMembers(undefined, activeQuery || undefined, false);
  const displayName = (m: Member) => m.name || m.email_address;

  const handleChangeRole = (member: Member, nextRole: StytchRoleId) => {
    setConfirmModal({ kind: "role-change", member, nextRole });
  };
  const handleDeactivate = (member: Member) => {
    setConfirmModal({ kind: "deactivate", member });
  };
  const handleCancelInvite = (member: Member) => {
    setConfirmModal({ kind: "cancel-invite", member });
  };
  const handleInviteClick = () => setInviteOpen(true);

  const renderConfirmModal = () => {
    if (!confirmModal) return null;
    const { kind, member } = confirmModal;

    if (kind === "role-change") {
      const nextRole = confirmModal.nextRole;
      const nextLevel = LEVEL_FOR_ROLE[nextRole];
      const nextLabel = LEVEL_LABEL[nextLevel];
      const currentLabel = LEVEL_LABEL[member.level];
      // Stytch's roles array is a full replacement, so this sets the member's
      // tier outright — it does not add to what they already hold. Naming both
      // the old and new tier keeps that legible at the point of no return.
      const title = "Change Role?";
      const body =
        `${displayName(member)} will change from ${currentLabel} to ${nextLabel}. ` +
        `Their access updates on their next request — Stytch re-resolves roles ` +
        `on every session authentication, so this does not wait for them to log ` +
        `out and back in.`;
      const cancelLabel = `Keep as ${currentLabel}`;
      const confirmLabel = `Change to ${nextLabel}`;
      const successMsg = `${displayName(member)} changed to ${nextLabel}`;
      return (
        <ConfirmModal
          title={title}
          body={body}
          icon="admin_panel_settings"
          confirmLabel={confirmLabel}
          cancelLabel={cancelLabel}
          destructive={false}
          onClose={() => setConfirmModal(null)}
          onConfirm={async () => {
            try {
              await updateMemberRole(
                member.member_id,
                nextRole,
                member.organization_id,
                getToken(),
              );
              setConfirmModal(null);
              pushToast("success", successMsg);
              reloadList();
            } catch {
              setConfirmModal(null);
              pushToast("error", "Failed to change role. Try again.");
            }
          }}
        />
      );
    }

    if (kind === "deactivate") {
      return (
        <ConfirmModal
          title="Deactivate Member?"
          body={`${displayName(member)} (${member.email_address}) will be permanently removed from this organization. They will immediately lose access. This cannot be undone — you must re-invite them if access is needed again.`}
          icon="person_remove"
          confirmLabel="Deactivate"
          cancelLabel="Keep Member"
          destructive
          onClose={() => setConfirmModal(null)}
          onConfirm={async () => {
            try {
              await deleteMember(member.member_id, member.organization_id, getToken());
              setConfirmModal(null);
              pushToast("success", "Member deactivated");
              reloadList();
            } catch {
              setConfirmModal(null);
              pushToast("error", "Failed to deactivate member. Try again.");
            }
          }}
        />
      );
    }

    // cancel-invite
    return (
      <ConfirmModal
        title="Cancel Invitation?"
        body={`The invitation sent to ${member.email_address} will be cancelled. The invite link will no longer work. You can re-invite this address at any time.`}
        icon="mail_off"
        confirmLabel="Cancel Invite"
        cancelLabel="Keep Invite"
        destructive
        onClose={() => setConfirmModal(null)}
        onConfirm={async () => {
          try {
            await deleteMember(member.member_id, member.organization_id, getToken());
            setConfirmModal(null);
            pushToast("success", "Invitation cancelled");
            reloadList();
          } catch {
            setConfirmModal(null);
            pushToast("error", "Failed to cancel invitation. Try again.");
          }
        }}
      />
    );
  };

  return (
    <>
      <span
        data-testid="admin-canary-badge"
        className="sr-only"
        aria-hidden="true"
      />

      <div className="mb-8">
        <h2 className="font-headline text-3xl font-bold tracking-tight text-on-surface mb-1">
          Member Management
        </h2>
        <p className="text-slate-500 font-label text-xs uppercase tracking-widest">
          Select a member to manage access
        </p>
      </div>

      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="relative">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by email..."
            className="w-64 px-3 py-2 bg-surface-container border border-outline-variant/30 rounded-md text-sm font-body text-on-surface placeholder-slate-500 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-colors"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-on-surface transition-colors"
              aria-label="Clear search"
            >
              <span className="material-symbols-outlined text-base">close</span>
            </button>
          )}
        </div>
        <button
          onClick={handleInviteClick}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-on-primary font-bold text-xs rounded-md uppercase tracking-tighter font-label transition-all active:opacity-80 shadow-[0_0_15px_-3px_rgba(60,221,199,0.4)]"
        >
          <span className="material-symbols-outlined text-base">person_add</span>
          Invite Member
        </button>
      </div>

      <MemberTable
        members={members}
        organizations={organizations}
        loading={loading}
        error={error}
        searchQuery={activeQuery}
        onRetry={() => fetchMembers(undefined, activeQuery || undefined, false)}
        onChangeRole={handleChangeRole}
        onDeactivate={handleDeactivate}
        onCancelInvite={handleCancelInvite}
      />

      {!loading && !error && members.length > 0 && (
        <>
          {nextCursor && (
            <button
              onClick={() => fetchMembers(nextCursor, activeQuery || undefined, true)}
              disabled={loadingMore}
              className="flex items-center gap-2 mx-auto mt-4 px-4 py-2 text-xs font-label font-bold uppercase tracking-wider text-slate-400 hover:text-on-surface border border-outline-variant/30 hover:border-outline-variant/50 rounded-md transition-colors disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-base">
                {loadingMore ? "hourglass_empty" : "expand_more"}
              </span>
              {loadingMore ? "Loading..." : "Load more"}
            </button>
          )}
          <p className="text-xs font-label text-slate-500 text-center mt-4">
            Showing {members.length} member{members.length === 1 ? "" : "s"}
          </p>
        </>
      )}

      {inviteOpen && (
        <InviteModal
          getToken={getToken}
          onClose={() => setInviteOpen(false)}
          onSuccess={(email) => {
            pushToast("success", `Invitation sent to ${email}`);
            reloadList();
          }}
          onError={(message) => pushToast("error", message)}
        />
      )}
      {renderConfirmModal()}

      <Toast toasts={toasts} />
    </>
  );
};

export default MembersTab;
